'use client';

import {
    type AnchorHTMLAttributes,
    type ComponentType,
    useId,
    useRef,
} from 'react';
import { RouterLink } from '@paypoteam/page-components/RouterLink';
import { Select, type SelectOption } from '@paypoteam/ui-components/Select';
import { Title } from '@paypoteam/ui-components/Title';
import { FilesIcon } from '@phosphor-icons/react/dist/ssr';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import type { ShareholdersNavigationProps } from '../ShareholdersSection.types';

import { SHAREHOLDERS_SECTION_CONTENT } from '@/static/content/pl/shareholders';

const getAriaCurrent = (isActive: boolean, isCurrentPage: boolean) => {
    if (!isActive) {
        return undefined;
    }

    return isCurrentPage ? 'page' : 'true';
};

export default function ShareholdersNavigation({
    navItems,
    'data-test-id': dataTestId,
}: ShareholdersNavigationProps) {
    const pathname = usePathname();
    const router = useRouter();
    const titleId = useId();
    const isSelectOpenRef = useRef(false);

    const activeItem = navItems.find(({ href }) => pathname?.startsWith(href));

    const handleValueChange = (slug: string) => {
        if (!isSelectOpenRef.current) {
            return;
        }

        const selectedItem = navItems.find(item => item.slug === slug);

        if (selectedItem) {
            router.push(selectedItem.href);
        }
    };

    return (
        <nav
            aria-labelledby={titleId}
            className="md:w-56 md:shrink-0 lg:w-[336px]"
        >
            <div
                className="md:hidden"
                data-test-id={dataTestId && `${dataTestId}-tabs-select`}
            >
                <Select
                    label={SHAREHOLDERS_SECTION_CONTENT.tabsLabel}
                    value={activeItem?.slug}
                    onValueChange={handleValueChange}
                    onOpenChange={isOpen => {
                        isSelectOpenRef.current = isOpen;
                    }}
                    options={navItems.map(({ slug, name }) => ({
                        value: slug,
                        children: name,
                        itemProps: {
                            'data-test-id':
                                dataTestId &&
                                `${dataTestId}-tabs-select-item-${slug}`,
                        } as SelectOption['itemProps'],
                    }))}
                />
            </div>

            <div className="hidden md:flex md:flex-col md:gap-4">
                <Title
                    id={titleId}
                    size="m"
                >
                    {SHAREHOLDERS_SECTION_CONTENT.tabsLabel}
                </Title>

                <ul className="flex flex-col gap-1">
                    {navItems.map(({ slug, name, href }) => {
                        const isActive = slug === activeItem?.slug;

                        return (
                            <li key={slug}>
                                <RouterLink
                                    LinkElement={
                                        Link as unknown as ComponentType<
                                            AnchorHTMLAttributes<HTMLAnchorElement>
                                        >
                                    }
                                    linkData={{ name, href }}
                                    variant={{
                                        color: isActive ? 'active' : 'primary',
                                        weight: 'bold',
                                        states: 'enable',
                                    }}
                                    aria-current={getAriaCurrent(
                                        isActive,
                                        pathname === href,
                                    )}
                                    data-test-id={
                                        dataTestId &&
                                        `${dataTestId}-sidebar-item-${slug}`
                                    }
                                    className="gap-3 rounded-md py-2 font-secondary text-sm"
                                >
                                    <FilesIcon
                                        size={32}
                                        aria-hidden
                                        className="shrink-0"
                                    />

                                    {name}
                                </RouterLink>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </nav>
    );
}
